import { useState, useEffect } from "react";
import clsx from 'clsx'
import { useIntl } from 'react-intl';
import { PageTitle, useLayout } from "../../../_metronic/layout/core";
import { DefaultTitle } from "../../../_metronic/layout/components/header/page-title/DefaultTitle";
import { IEmpresas } from "../../interfaces/IEmpresas";
import EmpresasService from "../../services/EmpresasService";
import Swal from 'sweetalert2';
import EmpresasModal from "./EmpresasModal";
import EmpresasTabela from "./EmpresasTabela";
import { useAuth } from "../../contexts/AuthProvider";

/*Tradução */
import './../../../i18n/config';
import { useTranslation } from 'react-i18next';

const Empresas = () => {
    const intl = useIntl();
    const [empresas, setEmpresas] = useState<IEmpresas[]>([]);
    const [showModal, setShowModal] = useState<boolean>(false);
    const [empresa, setEmpresa] = useState<IEmpresas | null>(null);
    const [modalTitle, setModalTitle] = useState<string>('');
    const [paginaCarregada, setPaginaCarregada] = useState<boolean>(false);
    const { classes } = useLayout();
    const { usuario } = useAuth();
    const { t } = useTranslation();

    const columns = [
        {
            name: t('Razão Social'),
            selector: (row: IEmpresas) => row['razaoSocial'],
            sortable: true,
        },
        {
            name: t('CNPJ'),
            selector: (row: IEmpresas) => row['cnpj'],
            sortable: true,
        },
        {
            name: t('Contato'),
            selector: (row: IEmpresas) => row['telefone'],
            sortable: true,
        },
        {
            name: t('Ações'),
            cell: (row: IEmpresas) => <AcoesEmpresas excluirEmpresa={excluirEmpresa} editarEmpresa={(empresa: IEmpresas) => {
                setModalTitle(t('Editar Empresa'));
                setEmpresa(empresa);
                toggleModal();
            }} item={row} />,
        }
    ];

    const AcoesEmpresas = ({ item, excluirEmpresa, editarEmpresa }: { item: IEmpresas, excluirEmpresa: (empresaId: number) => void, editarEmpresa: (empresa: IEmpresas) => void }) => {
        return <div data-tag="allowRowEvents">
            <i className="bi bi-slash-circle fs-1" style={{ color: '#5E6397', fontWeight: 'bold', marginRight: '20px', cursor: 'pointer' }}></i>
            <i onClick={() => { editarEmpresa(item) }} className="fas fa-edit fs-1" style={{ color: '#5E6397', fontWeight: 'normal', cursor: 'pointer' }}></i>
            <i onClick={() => { excluirEmpresa(item.id) }} className="fas fa-trash-alt fs-1" style={{ color: '#5E6397', marginLeft: '20px', fontWeight: 'normal', cursor: 'pointer' }}></i>
        </div>
    }

    useEffect(() => {
        async function obterTodasEmpresasIniciais() {
            try {
                await carregarTodasEmpresas()
            } catch (error) {
                console.log(error);
                Swal.fire({
                    text: t('Não foi possivel carregar as Empresas iniciais'),
                    timer: 4000,
                    icon: 'error',
                    showConfirmButton: false
                });
            }

            setPaginaCarregada(true);
        }

        obterTodasEmpresasIniciais();
    }, []);

    async function carregarTodasEmpresas() {
        if (usuario?.perfil === 'Analista') {
            const empresasIniciais = await EmpresasService.obterTodosPorUsuarioId(usuario.id);
            setEmpresas(empresasIniciais);
        } else {
            if (usuario?.clienteID) {
                const empresasIniciais = await EmpresasService.obterTodosPorClienteID(usuario.clienteID);
                setEmpresas(empresasIniciais);
            }
        }
    }

    async function toggleModal(carregarEmpresas?: boolean): Promise<void> {
        let show = !showModal;

        setShowModal(show);

        if (show === false) {
            if (carregarEmpresas) {
                setPaginaCarregada(false);
                await carregarTodasEmpresas();
                setPaginaCarregada(true);
            }
            setEmpresa(null);
        }
    }

    async function excluirEmpresa(empresaId: number) {
        try {
            const swalWithBootstrapButtons = Swal.mixin({
                customClass: {
                    confirmButton: 'btn btn-modal-excluir ms-5',
                    cancelButton: 'btn btn-modal-cancelar'
                },
                buttonsStyling: false
            });
            const result = await swalWithBootstrapButtons.fire({
                title: t('Excluir Empresa'),
                text: t(`Deseja realmente excluir esta empresa? Os relatórios e informações relacionados à essa empresa serão todos excluídos.`),
                showCancelButton: true,
                cancelButtonText: t('Cancelar'),
                confirmButtonText: t(`Excluir`),
                reverseButtons: true
            });

            if (result.isConfirmed) {
                await EmpresasService.excluir(empresaId);

                setEmpresas(old => old.filter(e => e.id !== empresaId));

                await Swal.fire({
                    title: '',
                    text: t('Empresa excluída com sucesso'),
                    icon: 'success',
                    showConfirmButton: false,
                    timer: 3000,
                })
            }
        } catch (error) {
            Swal.fire({
                text: t('Não foi possivel excluir.'),
                timer: 4000,
                icon: 'error',
                showConfirmButton: false
            });
        }
    }

    return <>
        <PageTitle breadcrumbs={[]}>{intl.formatMessage({ id: t('MENU.EMPRESAS') })}</PageTitle>
        <div className='toolbar' id='kt_toolbar'>
            <div id='kt_toolbar_container' className={clsx(classes.toolbarContainer.join(' '), 'd-flex flex-stack')}>
                <DefaultTitle />
                <EmpresasModal empresa={empresa} title={modalTitle} show={showModal} toggleModal={toggleModal} />
                <div className='d-flex align-items-center py-1'>
                    <button onClick={() => {
                        setModalTitle('Empresa');
                        setEmpresa(null);
                        setShowModal(true);
                    }} className='btn btn-sm btn-primario'>
                        <i className="icon-lg text-white fas fa-plus"></i>
                        <span style={{ verticalAlign: 'middle' }}> {t('EMPRESA')} </span>
                    </button>
                </div>
            </div>
        </div>

        {paginaCarregada ? <EmpresasTabela colunas={columns} itens={empresas} /> : <p>{t('Carregando...')}</p>}
    </>
};

export default Empresas;